interface GoogleCredentialResponse {
  credential: string;
}

interface AuthResponse {
  success: boolean;
  user?: {
    id: string;
    username: string;
    display_name: string;
    primary_email: string;
    image_url?: string;
  };
  tokens?: {
    access_token: string;
    refresh_token: string;
  };
  message?: string;
  code?: string;
}

class AuthService {
  private googleClientId = ''; // Will be set from environment or config
  private appleClientId = ''; // Will be set from environment or config

  async initializeAppleAuth(clientId: string) {
    console.log('🍎 Initializing Apple Sign-In with Client ID:', clientId);
    this.appleClientId = clientId;
    
    if (typeof window !== 'undefined' && window.AppleID) {
      console.log('✅ Apple Sign-In SDK loaded successfully');
      window.AppleID.auth.init({
        clientId: clientId,
        scope: 'name email',
        redirectURI: window.location.origin, // Dynamic - works on local, staging, production
        state: 'origin:web',
        usePopup: true  // Use popup flow
      });
      console.log('✅ Apple Sign-In initialized with redirect:', window.location.origin);
    } else {
      console.warn('⚠️ Apple Sign-In SDK not loaded yet');
    }
  }

  async initializeGoogleAuth(clientId: string) {
    console.log('🔐 Initializing Google OAuth with Client ID:', clientId);
    this.googleClientId = clientId;
    
    if (typeof window !== 'undefined' && window.google) {
      console.log('✅ Google Sign-In SDK loaded successfully');
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleGoogleResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      console.log('✅ Google Sign-In initialized with callback');
    } else {
      console.warn('⚠️ Google Sign-In SDK not loaded yet');
    }
  }

  async googleSignIn(): Promise<AuthResponse> {
    console.log('🚀 Starting Google Sign-In process...');
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.google) {
        console.log('✅ Google SDK available, setting up promise handlers');
        // Store the resolve/reject functions to be called by the callback
        this.currentPromise = { resolve, reject };
        
        console.log('📱 Attempting to show Google Sign-In prompt...');
        window.google.accounts.id.prompt((notification: any) => {
          console.log('📝 Google Sign-In prompt notification:', notification);
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('⚠️ Prompt not displayed or skipped, showing fallback button');
            // Fallback to manual sign-in if prompt fails
            this.showGoogleSignInButton();
          } else {
            console.log('✅ Google Sign-In prompt displayed successfully');
          }
        });
      } else {
        console.error('❌ Google Sign-In not initialized');
        reject(new Error('Google Sign-In not initialized'));
      }
    });
  }

  private currentPromise: { resolve: (value: AuthResponse) => void; reject: (reason?: any) => void } | null = null;

  private async handleGoogleResponse(response: GoogleCredentialResponse) {
    console.log('🎉 Google OAuth callback received!');
    console.log('🔑 Credential token length:', response.credential.length);
    
    try {
      console.log('🌐 Sending credential to beemi-server for verification...');
      const authResponse = await this.authenticateWithBeemiServer(response.credential);
      
      console.log('📥 Beemi-server response:', authResponse);
      
      // Check for authorization/authentication failures before checking tokens
      if (!authResponse.success) {
        console.error('❌ Authentication failed:', authResponse.message || authResponse.error);
        // Reject promise with the error message
        if (this.currentPromise) {
          this.currentPromise.resolve(authResponse); // Resolve with failed response
          this.currentPromise = null;
        }
        return; // Exit early to prevent page reload
      }
      
      if (authResponse.success && authResponse.tokens) {
        console.log('✅ Authentication successful!');
        console.log('👤 User:', authResponse.user);
        console.log('🎫 Tokens received - storing locally');
        
        // Store tokens FIRST and ensure they're saved before continuing
        this.storeTokens(authResponse.tokens);
        
        // Verify tokens are actually stored before proceeding
        const storedTokens = this.getStoredTokens();
        if (!storedTokens || !storedTokens.refreshToken) {
          console.error('❌ Failed to store tokens properly - retrying...');
          // Wait a bit and try storing again
          await new Promise(resolve => setTimeout(resolve, 100));
          this.storeTokens(authResponse.tokens);
          
          // Final verification
          const retriedTokens = this.getStoredTokens();
          if (!retriedTokens || !retriedTokens.refreshToken) {
            throw new Error('Failed to store authentication tokens');
          }
          console.log('✅ Tokens stored successfully on retry');
        } else {
          console.log('✅ Tokens verified in localStorage');
        }
        
        // Now safely get Firebase custom token and authenticate with Firebase
        try {
          console.log('🔥 Requesting Firebase custom token...');
          const firebaseToken = await this.getFirebaseCustomToken();
          
          if (firebaseToken) {
            console.log('🔐 Authenticating with Firebase...');
            const { firebaseAuthService } = await import('./firebaseAuthService');
            await firebaseAuthService.signInWithBeemiToken(firebaseToken);
            console.log('✅ Firebase authentication successful');
          }
        } catch (firebaseError) {
          console.warn('⚠️ Firebase authentication failed (will continue without):', firebaseError);
          // Don't fail the entire auth process if Firebase auth fails
        }
      }
      
      // Resolve promise with the response (already handled above for failed responses)
      if (this.currentPromise) {
        console.log('🔄 Resolving authentication promise');
        this.currentPromise.resolve(authResponse);
        this.currentPromise = null;
      }
    } catch (error) {
      console.error('💥 Google authentication error:', error);
      if (this.currentPromise) {
        console.log('🔄 Rejecting authentication promise with error');
        this.currentPromise.reject(error);
        this.currentPromise = null;
      }
    }
  }

  private showGoogleSignInButton() {
    // Create a temporary container for the Google sign-in button
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.zIndex = '9999';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.borderRadius = '8px';
    container.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    
    document.body.appendChild(container);

    if (window.google) {
      window.google.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        width: '300'
      });
    }

    // Remove the container after 10 seconds or when clicked outside
    setTimeout(() => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 10000);
  }

  private async authenticateWithBeemiServer(idToken: string): Promise<AuthResponse> {
    console.log('📦 Request payload: { idToken: [length=' + idToken.length + '], clientType: studio }');
    
    try {
      // Import the apiService here to avoid circular imports
      const { apiService } = await import('./api');
      
      // Send clientType to identify this as a Studio authentication request
      const response = await apiService.post<AuthResponse>('/auth/google', { 
        idToken,
        clientType: 'studio' 
      });
      console.log('✅ API Response Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API Error Response:', error.response?.data || error.message);
      // Extract user-friendly error message
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        // If server provided a message, use it directly (it's already user-friendly)
        throw new Error(serverMessage);
      } else {
        // Fallback to generic error with status code
        throw new Error(`Authentication failed: ${error.response?.status || 'Unknown'} - ${error.message}`);
      }
    }
  }

  async appleSignIn(): Promise<AuthResponse> {
    console.log('🚀 Starting Apple Sign-In process...');
    try {
      if (typeof window === 'undefined' || !window.AppleID) {
        throw new Error('Apple Sign-In not initialized');
      }

      console.log('📱 Showing Apple Sign-In popup...');
      const response = await window.AppleID.auth.signIn();
      
      console.log('🎉 Apple Sign-In response received');
      
      if (!response.authorization || !response.authorization.id_token) {
        throw new Error('No identity token received from Apple');
      }

      // Send to backend for verification
      const authResponse = await this.authenticateWithAppleBackend(
        response.authorization.id_token,
        response.user  // Only available on first sign-in
      );

      if (authResponse.success && authResponse.tokens) {
        console.log('✅ Apple authentication successful!');
        console.log('👤 User:', authResponse.user);
        
        // Store tokens
        this.storeTokens(authResponse.tokens);
        
        // Verify tokens are stored
        const storedTokens = this.getStoredTokens();
        if (!storedTokens || !storedTokens.refreshToken) {
          await new Promise(resolve => setTimeout(resolve, 100));
          this.storeTokens(authResponse.tokens);
        }
        
        // Authenticate with Firebase
        try {
          console.log('🔥 Requesting Firebase custom token...');
          const firebaseToken = await this.getFirebaseCustomToken();
          
          if (firebaseToken) {
            console.log('🔐 Authenticating with Firebase...');
            const { firebaseAuthService } = await import('./firebaseAuthService');
            await firebaseAuthService.signInWithBeemiToken(firebaseToken);
            console.log('✅ Firebase authentication successful');
          }
        } catch (firebaseError) {
          console.warn('⚠️ Firebase authentication failed:', firebaseError);
        }
      }

      return authResponse;
    } catch (error: any) {
      console.error('💥 Apple sign-in error:', error);
      
      // Handle user cancellation gracefully
      if (error.error === 'popup_closed_by_user') {
        throw new Error('Apple Sign-In was cancelled');
      }
      
      throw new Error(error?.message || 'Failed to sign in with Apple');
    }
  }

  private async authenticateWithAppleBackend(identityToken: string, user?: any): Promise<AuthResponse> {
    console.log('📦 Sending Apple token to beemi-server for verification');
    
    try {
      const { apiService } = await import('./api');
      
      const response = await apiService.post<AuthResponse>('/auth/apple', { 
        identityToken,
        user,  // Apple user data (name, email) - only on first sign-in
        clientType: 'studio'  // Important: identifies as web client
      });
      
      console.log('✅ Backend verification successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Backend verification failed:', error.response?.data || error.message);
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      throw new Error(`Apple authentication failed: ${error.response?.status || 'Unknown'}`);
    }
  }

  private storeTokens(tokens: { access_token: string; refresh_token: string }) {
    console.log('💾 Storing authentication tokens in localStorage');
    console.log('🔑 Access token length:', tokens.access_token.length);
    console.log('🔄 Refresh token length:', tokens.refresh_token.length);
    
    // Store tokens
    localStorage.setItem('beemi_access_token', tokens.access_token);
    localStorage.setItem('beemi_refresh_token', tokens.refresh_token);
    
    // Immediately verify storage worked
    const storedAccess = localStorage.getItem('beemi_access_token');
    const storedRefresh = localStorage.getItem('beemi_refresh_token');
    
    console.log('🔍 Token storage verification:', {
      accessTokenStored: !!storedAccess,
      refreshTokenStored: !!storedRefresh,
      accessTokenMatches: storedAccess === tokens.access_token,
      refreshTokenMatches: storedRefresh === tokens.refresh_token,
      localStorageAvailable: typeof Storage !== 'undefined'
    });
    
    if (!storedAccess || !storedRefresh) {
      console.error('❌ Failed to store tokens in localStorage');
      throw new Error('Failed to store authentication tokens');
    }
  }

  getStoredTokens() {
    const accessToken = localStorage.getItem('beemi_access_token');
    const refreshToken = localStorage.getItem('beemi_refresh_token');
    
    console.log('🔍 Token retrieval check:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenLength: accessToken?.length || 0,
      refreshTokenLength: refreshToken?.length || 0,
      allLocalStorageKeys: Object.keys(localStorage)
    });
    
    if (accessToken && refreshToken) {
      console.log('✅ Retrieved stored tokens from localStorage');
      return { accessToken, refreshToken };
    } else {
      console.log('❌ No stored tokens found in localStorage');
      console.log('🧐 Missing tokens details:', {
        accessTokenMissing: !accessToken,
        refreshTokenMissing: !refreshToken
      });
      return null;
    }
  }

  clearTokens() {
    console.log('🧹 Clearing authentication tokens from localStorage');
    localStorage.removeItem('beemi_access_token');
    localStorage.removeItem('beemi_refresh_token');
  }

  async getCurrentUser(): Promise<any> {
    console.log('👤 Getting current user from beemi-server...');
    const tokens = this.getStoredTokens();
    if (!tokens) {
      console.error('❌ No authentication tokens found');
      throw new Error('No authentication tokens found');
    }

    try {
      // Import the apiService here to avoid circular imports
      const { apiService } = await import('./api');
      
      const response = await apiService.get('/auth/me');
      console.log('✅ Current user retrieved:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get current user:', error.response?.data || error.message);
      throw new Error(`Failed to get current user: ${error.response?.status || 'Unknown'}`);
    }
  }

  async updateProfile(profileData: {
    display_name?: string;
    image_url?: string;
    username?: string;
    bio?: string;
    website_url?: string;
  }): Promise<any> {
    console.log('📝 Updating user profile...', profileData);
    const tokens = this.getStoredTokens();
    if (!tokens) {
      console.error('❌ No authentication tokens found');
      throw new Error('No authentication tokens found');
    }

    try {
      const { apiService } = await import('./api');
      
      const response = await apiService.put('/auth/profile', profileData);
      console.log('✅ Profile updated successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to update profile:', error.response?.data || error.message);
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getStoredTokens();
  }

  /**
   * Get Firebase custom token from beemi-server for secure storage access
   */
  async getFirebaseCustomToken(): Promise<string | null> {
    console.log('🔥 Requesting Firebase custom token from beemi-server...');
    const tokens = this.getStoredTokens();
    if (!tokens) {
      console.error('❌ No authentication tokens found for Firebase token request');
      return null;
    }

    try {
      const { apiService } = await import('./api');
      const response = await apiService.get<{ success: boolean; firebase_token: string }>('/auth/firebase-token');
      
      if (response.data.success && response.data.firebase_token) {
        console.log('✅ Firebase custom token received from beemi-server');
        return response.data.firebase_token;
      } else {
        console.error('❌ Failed to get Firebase custom token:', response.data);
        return null;
      }
    } catch (error: any) {
      console.error('❌ Failed to get Firebase custom token:', error.response?.data || error.message);
      return null;
    }
  }

  logout() {
    // Track logout event before clearing data
    import('./analyticsService').then(({ analyticsService }) => {
      analyticsService.track({
        name: 'user_logged_out',
        properties: {
          source: 'web'
        }
      });
    }).catch(err => console.warn('Failed to track logout:', err));
    
    this.clearTokens();
    // Optionally, you can also sign out from Google
    if (typeof window !== 'undefined' && window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

// Global Google and Apple types
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          disableAutoSelect: () => void;
        };
      };
    };
    AppleID: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

export default new AuthService(); 
